import {
  outline,
  surfaceDim,
  onSurfaceVariant,
  surfaceBright,
  onSurface,
  primary,
  onSurfaceDisabled,
  onSurfacePlaceholder,
} from 'core/styles'

const baseContainerStyles = (provided, state) => {
  const { selectProps } = state || {}
  const { classNamePrefix } = selectProps || {}
  return {
    ...provided,
    fontSize: classNamePrefix === 'inline' ? '14px' : '16px',
  }
}

export const baseControlStyles = (provided, state) => {
  const { isFocused, menuIsOpen, selectProps } = state || {}
  const { classNamePrefix } = selectProps || {}

  return {
    ...provided,
    alignItems: 'normal',
    cursor: 'pointer',
    borderRadius: '8px',
    border: `1px solid ${outline}`,
    padding: '0',
    height: '32px',
    minHeight: '32px',
    fontSize: '14px',
    fontWeight: 500,
    ...(isFocused || menuIsOpen ?
      {
        /**
         * Using !important to override the hover styles
         * for inline drop downs, since those styles are
         * slightly more specific than this.
         */
        borderColor: `${primary} !important`,
        boxShadow: 'none',
      }
    : classNamePrefix === 'inline' ?
      {
        '&:hover': { borderColor: 'transparent' },
        borderColor: 'transparent',
        minHeight: '0',
      }
    : {
        borderColor: outline,
        boxShadow: 'none',
        '&:hover': { borderColor: outline },
      }),
  }
}

const baseDropdownIndicator = (provided, state) => {
  const { selectProps } = state || {}
  const { classNamePrefix } = selectProps || {}

  const stylesForInlineDropdown = classNamePrefix === 'inline' ? { padding: '0' } : {}

  return {
    ...provided,
    ...stylesForInlineDropdown,
  }
}

const baseIndicatorSeparatorStyles = (provided) => ({
  ...provided,
  display: 'none',
  paddingLeft: '0',
})

const baseMenuStyles = (provided, _state) => ({
  ...provided,
  display: 'inline-flex',
  overflow: 'hidden',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  borderRadius: '12px',
  border: `1px solid ${outline}`,
  backgroundColor: outline,
  width: 'auto',
  fontSize: '14px',
  fontWeight: 500,
})

const baseMenuListStyles = (provided, _state) => ({
  ...provided,
  '::-webkit-scrollbar': {
    WebkitAppearance: 'none',
    width: '12px',
  },
  '::-webkit-scrollbar-thumb': {
    borderRadius: '8px',
    backgroundColor: onSurfaceDisabled,
    border: '3px solid white',
  },
  padding: 0,
  display: 'grid',
  gap: '1px',
})

const baseMultiValueLabelStyles = (provided, state) => {
  const { selectProps } = state || {}
  const { wrap } = selectProps || {}
  const stylesForWrap = {
    whiteSpace: wrap ? 'wrap' : 'nowrap',
  }
  return {
    ...provided,
    ...stylesForWrap,
  }
}

const baseOptionStyles = (provided, state) => ({
  ...provided,
  display: 'flex',
  svg: {
    fill: onSurface,
  },
  cursor: state.isDisabled ? 'not-allowed' : 'pointer',
  padding: '6px',
  backgroundColor:
    state.isSelected ? surfaceBright
    : state.isFocused ? surfaceDim
    : surfaceBright,
  color: state.isSelected ? onSurface : onSurfaceVariant,
  textAlign: 'center',
  ':hover': {
    backgroundColor: state.isDisabled ? 'transparent' : surfaceDim,
  },
})

const basePlaceholderStyles = (provided, state) => {
  const { selectProps } = state || {}
  const { classNamePrefix } = selectProps || {}

  const stylesForInlineDropdown = classNamePrefix === 'inline' ? { fontSize: 'inherit' } : {}

  return {
    ...provided,
    // react-select default overrides
    position: 'relative',
    top: '0',
    transform: 'none',
    margin: '0',
    // end react-select overrides ---
    color: onSurfacePlaceholder,
    whiteSpace: 'nowrap',
    ...stylesForInlineDropdown,
  }
}

const baseSingleValueStyles = (provided, state) => {
  const { selectProps } = state || {}
  const { classNamePrefix } = selectProps || {}

  const stylesForInlineDropdown =
    classNamePrefix === 'inline' ?
      {
        maxWidth: 'none',
        color: 'inherit',
      }
    : {}

  return {
    ...provided,
    display: 'flex',
    alignItems: 'center',
    marginLeft: '0',
    marginRight: '0',
    maxWidth: 'none',
    ...stylesForInlineDropdown,
  }
}

const baseValueContainerStyles = (provided, state) => {
  const { selectProps } = state || {}
  const { classNamePrefix, wrap } = selectProps || {}

  const stylesForInlineDropdown =
    classNamePrefix === 'inline' ?
      {
        fontSize: 'inherit',
        /**
         * Using 4px top/bottom padding to split the 8px padding
         * that was originally on the Row component, which now
         * has 4px top/bottom padding and is used to wrap this
         * component.
         */
      }
    : {}

  const stylesForWrap = {
    flexWrap: wrap ? 'wrap' : 'nowrap',
  }

  return {
    ...provided,
    padding: '2px 0 2px 8px',
    ...stylesForInlineDropdown,
    ...stylesForWrap,
  }
}

const customStyles = {
  container: (provided, state) => baseContainerStyles(provided, state),
  control: (provided, state) => baseControlStyles(provided, state),
  dropdownIndicator: (provided, state) => baseDropdownIndicator(provided, state),
  indicatorSeparator: (provided) => baseIndicatorSeparatorStyles(provided),
  menu: (provided, state) => baseMenuStyles(provided, state),
  menuList: (provided, state) => baseMenuListStyles(provided, state),
  multiValueLabel: (provided, state) => baseMultiValueLabelStyles(provided, state),
  option: (provided, state) => baseOptionStyles(provided, state),
  placeholder: (provided, state) => basePlaceholderStyles(provided, state),
  singleValue: (provided, state) => baseSingleValueStyles(provided, state),
  valueContainer: (provided, state) => baseValueContainerStyles(provided, state),
}

export default customStyles
